import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  EyeOff,
  Ghost,
  Cpu,
  Battery,
  MemoryStick,
  Smartphone,
  ShoppingBag,
  Check,
  Sparkles,
  Palette,
  ExternalLink,
} from "lucide-react";
import {
  PIXELS,
  TIERS,
  BOOT_KEYS,
  ACCESSORIES,
  DESIGN_STYLES,
  MOODBOARD_URL,
  formatAUD,
  priceFor,
} from "../mock";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";

const tierIcon = { degoogled: ShieldCheck, stealth: EyeOff, phantom: Ghost };

const ProductDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const phone = PIXELS.find((p) => p.id === id);
  const accessory = ACCESSORIES.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (phone) return <PhonePage product={phone} nav={nav} />;
  if (accessory) return <AccessoryPage product={accessory} nav={nav} />;

  return (
    <main className="min-h-screen bg-black text-white pt-32 px-6 max-w-3xl mx-auto text-center">
      <h1 className="font-headline text-4xl">Product not found</h1>
      <Link to="/shop" className="btn-primary mt-8 inline-flex">
        Back to shop
      </Link>
    </main>
  );
};

/* -------------------- PHONE VIEW -------------------- */
const PhonePage = ({ product, nav }) => {
  const { addItem } = useCart();
  const { toast } = useToast();
  const [tierId, setTierId] = useState("stealth");
  const [storage, setStorage] = useState(product.storage[0]);

  const price = useMemo(() => priceFor(product.base_aud, tierId), [product, tierId]);
  const tier = TIERS.find((t) => t.id === tierId);

  const handleAdd = () => {
    addItem({
      productId: product.id,
      productName: product.name,
      kind: "phone",
      tierId,
      tierName: tier.name,
      variant: storage,
      storage,
      price,
      image: product.image,
      qty: 1,
    });
    toast({ title: "Added to bag", description: `${product.name} · ${tier.name} · ${storage}` });
  };

  return (
    <main className="min-h-screen bg-black text-white pt-24">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="grid md:grid-cols-2 gap-10 mt-6">
          <div className="relative rounded-3xl overflow-hidden card-glass aspect-[4/5]">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${product.image})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute top-5 left-5 chip">Pixel {product.year}</div>
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em] text-purple-300">{tier.name} tier</div>
              <div className="font-display text-xl text-white">{formatAUD(price)}</div>
            </div>
          </div>

          <div>
            <h1 className="font-headline text-4xl md:text-5xl font-bold text-gradient-purple leading-[0.95]">
              {product.name}
            </h1>
            <p className="mt-3 text-white/60">{product.tagline}</p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <SpecPill icon={Cpu} label="Chip" value={product.chip} />
              <SpecPill icon={Smartphone} label="Display" value={product.display} />
              <SpecPill icon={MemoryStick} label="RAM" value={product.ram} />
              <SpecPill icon={Battery} label="Battery" value={product.battery} />
            </div>

            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">Storage</div>
              <div className="flex gap-2 flex-wrap">
                {product.storage.map((s) => (
                  <button key={s} onClick={() => setStorage(s)} className={`px-4 py-2 rounded-full text-sm border transition-all ${storage === s ? "bg-purple-500 text-black border-purple-400" : "bg-white/5 text-white/80 border-white/10 hover:border-purple-500/50"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">Privacy tier</div>
              <div className="grid grid-cols-1 gap-3">
                {TIERS.map((t) => {
                  const Ico = tierIcon[t.id];
                  const active = tierId === t.id;
                  return (
                    <button key={t.id} onClick={() => setTierId(t.id)} className={`text-left rounded-2xl p-4 border transition-all flex items-center justify-between gap-4 ${active ? "border-purple-500/60 bg-purple-500/5" : "border-white/10 bg-white/[0.02] hover:border-purple-500/30"}`}>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                          <Ico className="h-5 w-5" style={{ color: t.color }} />
                        </div>
                        <div>
                          <div className="font-display font-semibold text-white flex items-center gap-2">
                            {t.name}
                            {active && <Check className="h-4 w-4 text-purple-300" />}
                          </div>
                          <div className="text-xs text-white/50">{t.tagline}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white/50">+{formatAUD(t.price_add)}</div>
                        <div className="font-display text-lg font-bold text-white mt-0.5">{formatAUD(priceFor(product.base_aud, t.id))}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/40">Total</div>
                <div className="font-display text-3xl font-bold text-white">{formatAUD(price)}</div>
                <div className="text-xs text-white/40 mt-1">Base {formatAUD(product.base_aud)} + {tier.name} tier {formatAUD(tier.price_add)}</div>
              </div>
              <div className="flex gap-3">
                <button onClick={handleAdd} className="btn-primary">
                  <ShoppingBag className="h-4 w-4" /> Add to bag
                </button>
                <Link to="/tiers" className="btn-ghost">Compare tiers</Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 card-glass rounded-3xl p-8">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
              {React.createElement(tierIcon[tier.id], { className: "h-5 w-5", style: { color: tier.color } })}
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/40">What's included · {tier.name}</div>
              <div className="font-display text-2xl font-bold" style={{ color: tier.color }}>{tier.tagline}</div>
            </div>
          </div>
          <p className="mt-4 text-white/70">{tier.description}</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {tier.highlights.map((h) => (
              <div key={h} className="flex items-start gap-3 text-sm text-white/80">
                <span className="mt-0.5 h-5 w-5 rounded-full bg-purple-500/15 border border-purple-500/40 flex items-center justify-center flex-shrink-0">
                  <Check className="h-3 w-3 text-purple-300" />
                </span>
                {h}
              </div>
            ))}
          </div>
        </div>

        {BOOT_KEYS[product.id] && (
          <div className="mt-6 card-glass rounded-3xl p-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-purple-300" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/40">Verified boot fingerprint</div>
                <div className="font-display font-semibold text-white mt-0.5">GrapheneOS Auditor · {product.name}</div>
              </div>
            </div>
            <div className="font-mono text-lg text-purple-300 tracking-wider select-all bg-black/40 border border-purple-500/20 rounded-xl px-4 py-2">
              {BOOT_KEYS[product.id]}
            </div>
          </div>
        )}

        <RelatedGrid excludeId={product.id} />
      </div>
    </main>
  );
};

/* -------------------- ACCESSORY VIEW -------------------- */
const AccessoryPage = ({ product, nav }) => {
  const { addItem } = useCart();
  const { toast } = useToast();
  const [variant, setVariant] = useState(product.variants[0]);
  const [styleId, setStyleId] = useState(product.hasStyle ? DESIGN_STYLES[0].id : null);
  const [showBoard, setShowBoard] = useState(false);

  const styleLabel = styleId ? DESIGN_STYLES.find((s) => s.id === styleId)?.label : null;

  const handleAdd = () => {
    const variantWithStyle = styleLabel ? `${variant.name} · ${styleLabel}` : variant.name;
    addItem({
      productId: product.id,
      productName: product.name,
      kind: "accessory",
      variant: variantWithStyle,
      storage: variantWithStyle,
      styleId,
      price: variant.price,
      image: product.image,
      qty: 1,
    });
    toast({ title: "Added to bag", description: `${product.name} · ${variantWithStyle}` });
  };

  return (
    <main className="min-h-screen bg-black text-white pt-24">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="grid md:grid-cols-2 gap-10 mt-6">
          <div className="relative rounded-3xl overflow-hidden card-glass aspect-[4/5]">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${product.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                ...(product.imageStyle || {}),
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute top-5 left-5 chip"><Sparkles className="h-3 w-3" /> Phantom accessory</div>
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em] text-purple-300">{variant.name}</div>
              <div className="font-display text-xl text-white">{formatAUD(variant.price)}</div>
            </div>
          </div>

          <div>
            <h1 className="font-headline text-4xl md:text-5xl font-bold text-gradient-purple leading-[0.95]">
              {product.name}
            </h1>
            <p className="mt-3 text-white/60">{product.tagline}</p>
            <p className="mt-6 text-white/70 leading-relaxed">{product.description}</p>

            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">{product.variantLabel}</div>
              <div className="flex gap-2 flex-wrap">
                {product.variants.map((v) => (
                  <button key={v.name} onClick={() => setVariant(v)} className={`px-4 py-2 rounded-full text-sm border transition-all inline-flex items-center gap-2 ${variant.name === v.name ? "bg-purple-500 text-black border-purple-400" : "bg-white/5 text-white/80 border-white/10 hover:border-purple-500/50"}`}>
                    {v.name}
                    <span className={variant.name === v.name ? "text-black/70" : "text-white/40"}>· {formatAUD(v.price)}</span>
                  </button>
                ))}
              </div>
            </div>

            {product.hasStyle && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40 flex items-center gap-2">
                    <Palette className="h-3.5 w-3.5" /> Design style
                  </div>
                  <button onClick={() => setShowBoard(true)} className="text-xs text-purple-300 hover:text-white inline-flex items-center gap-1">
                    View design sheet <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {DESIGN_STYLES.map((s) => (
                    <button key={s.id} onClick={() => setStyleId(s.id)} className={`px-3.5 py-1.5 rounded-full text-xs border transition-all ${styleId === s.id ? "bg-purple-500 text-black border-purple-400" : "bg-white/5 text-white/70 border-white/10 hover:border-purple-500/40 hover:text-white"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="mt-3 text-[11px] text-white/40">
                  Not sure? Open the design sheet and pick the look that matches your vibe.
                </div>
              </div>
            )}

            <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/40">Total</div>
                <div className="font-display text-3xl font-bold text-white">{formatAUD(variant.price)}</div>
                <div className="text-xs text-white/40 mt-1">
                  {product.variantLabel}: {variant.name}{styleLabel ? ` · ${styleLabel}` : ""}
                </div>
              </div>
              <button onClick={handleAdd} className="btn-primary">
                <ShoppingBag className="h-4 w-4" /> Add to bag
              </button>
            </div>
          </div>
        </div>

        <RelatedAccessories excludeId={product.id} />

        {showBoard && (
          <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-6 overflow-y-auto" onClick={() => setShowBoard(false)}>
            <div className="relative max-w-6xl w-full my-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="chip"><Palette className="h-3 w-3" /> Design sheet</div>
                  <div className="mt-2 font-headline text-2xl text-white">Phantom style library</div>
                </div>
                <button onClick={() => setShowBoard(false)} className="h-10 w-10 rounded-full border border-white/10 text-white/70 hover:text-white">×</button>
              </div>
              <img src={MOODBOARD_URL} alt="Phantom Industries design styles" className="w-full rounded-2xl border border-white/10" />
              <div className="mt-3 text-xs text-white/40 text-center">
                Reference the labels above the samples when picking your style at checkout.
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

const SpecPill = ({ icon: Icon, label, value }) => (
  <div className="card-glass rounded-xl p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className="mt-1 text-sm text-white/90">{value}</div>
  </div>
);

const RelatedGrid = ({ excludeId }) => (
  <div className="mt-16">
    <div className="flex items-end justify-between mb-6">
      <h2 className="font-headline text-2xl md:text-3xl font-bold text-white">Also worth considering</h2>
      <Link to="/shop" className="text-sm text-purple-300 hover:text-white inline-flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {PIXELS.filter((p) => p.id !== excludeId).slice(0, 4).map((p) => (
        <Link key={p.id} to={`/product/${p.id}`} className="card-glass rounded-2xl overflow-hidden hover-lift group">
          <div className="aspect-square relative">
            <div className="absolute inset-0 bg-cover bg-center opacity-70 group-hover:opacity-100 transition-opacity" style={{ backgroundImage: `url(${p.image})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          </div>
          <div className="p-4">
            <div className="font-display font-semibold text-white">{p.name}</div>
            <div className="text-xs text-white/50 mt-1">From {formatAUD(priceFor(p.base_aud, "degoogled"))}</div>
          </div>
        </Link>
      ))}
    </div>
  </div>
);

const RelatedAccessories = ({ excludeId }) => {
  const others = ACCESSORIES.filter((a) => a.id !== excludeId).slice(0, 4);
  return (
    <div className="mt-16">
      <div className="flex items-end justify-between mb-6">
        <h2 className="font-headline text-2xl md:text-3xl font-bold text-white">More accessories</h2>
        <Link to="/shop" className="text-sm text-purple-300 hover:text-white inline-flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {others.map((a) => (
          <Link key={a.id} to={`/product/${a.id}`} className="card-glass rounded-2xl overflow-hidden hover-lift group">
            <div className="aspect-square relative">
              <div
                className="absolute inset-0 opacity-90 group-hover:opacity-100 transition-opacity"
                style={{
                  backgroundImage: `url(${a.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  ...(a.imageStyle || {}),
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </div>
            <div className="p-4">
              <div className="font-display font-semibold text-white">{a.name}</div>
              <div className="text-xs text-white/50 mt-1">From {formatAUD(Math.min(...a.variants.map((v) => v.price)))}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ProductDetail;
